import { useState } from 'react';
import { Link } from 'react-router-dom';
import { useCardEpics } from '../hooks/useCardEpics';
import type { EpicBasic } from '../types/kanban';
import PlusIcon from './icons/PlusIcon';
import UnlinkIcon from './icons/UnlinkIcon';
import ChevronRightIcon from './icons/ChevronRightIcon';
import CheckIcon from './icons/CheckIcon';

interface CardEpicsProps {
  cardEpics: EpicBasic[];
  boardId: number;
  cardId: number;
  refetchCard: () => void;
}

export function CardEpics({ cardEpics, boardId, cardId, refetchCard }: CardEpicsProps) {
  const [isDropdownOpen, setIsDropdownOpen] = useState(false);

  const { epics, addEpic, removeEpic } = useCardEpics(boardId, cardId);

  const handleAddEpic = async (epicId: number) => {
    const success = await addEpic(epicId);
    if (success) {
      await refetchCard();
      setIsDropdownOpen(false);
    }
  };

  const handleRemoveEpic = async (epicId: number, epicName: string) => {
    if (!confirm(`Unlink epic "${epicName}" from this card?`)) {
      return;
    }

    const success = await removeEpic(epicId);
    if (success) {
      await refetchCard();
    }
  };

  // Filter out epics that are already linked to the card
  const availableEpics = epics.filter(
    (epic) => !cardEpics.some((cardEpic) => cardEpic.id === epic.id)
  );

  return (
    <div className="bg-gray-800 rounded-lg overflow-hidden">
      {/* Header */}
      <div className="px-4 py-3 border-b border-gray-700 flex justify-between items-center">
        <h2 className="text-lg font-semibold text-white">Epics</h2>
        <span className="text-sm text-gray-400">{cardEpics.length} linked</span>
      </div>

      {/* Epics List */}
      <div className="divide-y divide-gray-700 max-h-[400px] overflow-y-auto scrollbar-custom">
        {cardEpics.length > 0 ? (
          cardEpics.map((epic: EpicBasic) => (
            <EpicListItem
              key={epic.id}
              epic={epic}
              boardId={boardId}
              onUnlink={() => handleRemoveEpic(epic.id, epic.name)}
            />
          ))
        ) : (
          <div className="p-6 text-center">
            <div className="text-4xl mb-2">📋</div>
            <p className="text-gray-400 text-sm">No epics linked yet</p>
          </div>
        )}
      </div>

      {/* Add Epic Button */}
      {availableEpics.length > 0 && (
        <div className="px-4 py-3 border-t border-gray-700 relative">
          <button
            onClick={() => setIsDropdownOpen(!isDropdownOpen)}
            className="w-full flex items-center justify-center gap-2 text-gray-400 hover:text-white hover:bg-gray-700 px-3 py-2 rounded text-sm transition-colors"
          >
            <span className="w-4 h-4">
              <PlusIcon />
            </span>
            {cardEpics.length > 0 ? 'Link another epic' : 'Link epic'}
          </button>

          {/* Dropdown */}
          {isDropdownOpen && (
            <div className="absolute bottom-full left-4 right-4 mb-2 bg-gray-700 rounded-lg shadow-lg border border-gray-600 max-h-[200px] overflow-y-auto scrollbar-custom z-10">
              {availableEpics.map((epic: EpicBasic) => (
                <button
                  key={epic.id}
                  onClick={() => handleAddEpic(epic.id)}
                  className="w-full text-left px-3 py-2 hover:bg-gray-600 transition-colors flex items-center gap-3 border-b border-gray-600 last:border-b-0"
                >
                  <span
                    className="w-3 h-3 rounded-full flex-shrink-0"
                    style={{ backgroundColor: epic.color || '#3b82f6' }}
                  />
                  <span className="text-white text-sm truncate flex-1">{epic.name}</span>
                  <span className="w-4 h-4 text-green-400 flex-shrink-0">
                    <CheckIcon />
                  </span>
                </button>
              ))}
            </div>
          )}
        </div>
      )}
    </div>
  );
}

interface EpicListItemProps {
  epic: EpicBasic;
  boardId: number;
  onUnlink: () => void;
}

function EpicListItem({ epic, boardId, onUnlink }: EpicListItemProps) {
  const defaultColor = '#3b82f6';
  const color = epic.color || defaultColor;

  return (
    <div className="group relative">
      <Link
        to={`/boards/${boardId}/epics/${epic.id}`}
        className="block px-4 py-2.5 hover:bg-gray-750 transition-colors"
      >
        <div className="flex items-center gap-3">
          {/* Color Indicator */}
          <div className="w-2 h-2 rounded-full flex-shrink-0" style={{ backgroundColor: color }} />

          {/* Epic Name */}
          <h3 className="text-white text-sm font-medium truncate flex-1">{epic.name}</h3>

          {/* Chevron indicator */}
          <span className="w-4 h-4 text-gray-600 group-hover:text-gray-400 transition-colors flex-shrink-0">
            <ChevronRightIcon />
          </span>
        </div>
      </Link>

      {/* Unlink Button (visible on hover) */}
      <button
        onClick={(e) => {
          e.preventDefault();
          e.stopPropagation();
          onUnlink();
        }}
        className="absolute right-2 top-1/2 -translate-y-1/2 opacity-0 group-hover:opacity-100 transition-opacity bg-gray-800 hover:bg-red-900/50 text-gray-400 hover:text-red-400 p-1 rounded border border-gray-700 hover:border-red-800"
        title="Unlink epic"
      >
        <span className="w-3 h-3">
          <UnlinkIcon />
        </span>
      </button>
    </div>
  );
}
